import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchEventShifts } from '@/utils/eventShiftsApi';
import type { EventShift } from '@/utils/eventShifts';

/**
 * An event's shifts with fill counts. `shifts` stays empty while loading and when the read
 * fails; `failed` tells the two apart. `loadedFor` is the event whose read has finished, so a
 * caller can wait for this event's result rather than act on the empty first render.
 */
export function useEventShifts(eventId: string | null | undefined, enabled = true) {
  const [shifts, setShifts] = useState<EventShift[]>([]);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [loadedFor, setLoadedFor] = useState<string | null>(null);
  const eventIdRef = useRef(eventId);
  eventIdRef.current = eventId;

  const load = useCallback(async () => {
    const requestedId = eventId;
    if (!requestedId || !enabled) {
      setShifts([]);
      setFailed(false);
      return;
    }
    setLoading(true);
    const result = await fetchEventShifts(requestedId);
    // A slower response for an event the caller has since moved away from must not win.
    if (eventIdRef.current !== requestedId) return;
    setShifts(result ?? []);
    setFailed(result === null);
    setLoading(false);
    setLoadedFor(requestedId);
  }, [eventId, enabled]);

  useEffect(() => {
    setShifts([]);
    void load();
  }, [load]);

  return { shifts, loading, failed, loadedFor, refetch: load };
}
